import React, { useEffect, useState, useRef, useMemo } from 'react';
import * as XLSX from 'xlsx';
import { useUserProfile } from '../masterLayout/MasterLayoutBody';
import {
  createGroup,
  updateGroup,
  deleteGroup,
  getPaginatedGroups,
  checkGroupNameExists,
} from '../api/groupAPI';
import { Icon } from '@iconify/react';
import { useFormBuilder } from '../utils/useFormBuilder';
import { validateForm } from '../utils/useValidator';
import { useTableFilters } from '../utils/useTableFilters';
import GroupDataTable from './GroupDataTable';

const GroupListLayer = () => {
  const userProfile = useUserProfile();
  const [groupList, setGroupList] = useState([]);
  const [groupPage, setGroupPage] = useState(1);
  const [groupHasMore, setGroupHasMore] = useState(true);
  const [groupLoading, setGroupLoading] = useState(false);
  const [newGroup, setNewGroup] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [errors, setErrors] = useState({});
  const listContainerRef = useRef(null);

  const fetchGroups = async () => {
    setGroupLoading(true);
    try {
      const pageSize = groupPage === 1 ? 25 : 5;
      const resp = await getPaginatedGroups({
        page: groupPage,
        pageSize,
        search: searchTerm,
        manufacturerId: appliedFilters.manufacturerId,
        classId: appliedFilters.classId,
      });
      setGroupList((prev) => [...prev, ...resp.data]);
      setGroupHasMore(groupPage * pageSize < resp.total);
    } catch (err) {
      console.error('Error fetching statuses:', err);
    } finally {
      setGroupLoading(false);
    }
  };


  const inputFields = useMemo(
    () => [
      { field: 'groupName', label: 'Model or Group Name', type: 'text', size: 6, required: true },
      { field: 'imageBlock1', label: 'Image', type: 'emptyBorder', size: 6, required: false },
      { field: 'classId', label: 'Class', type: 'select', size: 6, api: 'class', displayField: 'className', required: true },
      { field: 'generalSpacer1', label: '', type: 'empty', size: 6, required: false },
      { field: 'manufacturerId', label: 'Manufacturer', type: 'select', size: 6, api: 'manufacturers', displayField: 'manufacturer', required: true },
      { field: 'generalSpacer2', label: '', type: 'empty', size: 6, required: false },
      { field: 'groupImage', label: 'Group Image', type: 'image', size: 6, directory: 'modelimages', showImage: false },
      { field: 'generalSpacer3', label: '', type: 'empty', size: 6, required: false },
      { field: 'groupDescription', label: 'Description', type: 'textarea', size: 6 },
      
    ],
    []
  );

  const { renderField } = useFormBuilder(inputFields, newGroup, setNewGroup, errors);

  const tableFilters = useMemo(
    () => [
      { field: 'manufacturerId', type: 'dropDown', label: 'Manufacturer', api: 'manufacturers', displayField: 'manufacturer' },
      { field: 'classId', type: 'dropDown', label: 'Class', api: 'class', displayField: 'className' },
    ],
    [],
  );

  const {
    appliedFilters,
    showDropdown,
    setShowDropdown,
    renderFilterDropdown,
    renderFilterBadges,
    resetFilters,
    applyFilters,
  } = useTableFilters(tableFilters);

  const runValidation = () => {
    const newErrors = validateForm(inputFields, newGroup);
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };


  useEffect(() => {
    fetchGroups();
  }, [groupPage, appliedFilters, searchTerm]);

  useEffect(() => {
    setGroupList([]);
    setGroupPage(1);
    setGroupHasMore(true);
  }, [appliedFilters, searchTerm]);

  useEffect(() => {
    const container = listContainerRef.current;
    if (!container) return;

    const onScroll = () => {
      if (
        container.scrollTop + container.clientHeight >= container.scrollHeight - 5 &&
        groupHasMore &&
        !groupLoading
      ) {
        setGroupPage((p) => p + 1);
      }
    };

    container.addEventListener('scroll', onScroll);
    return () => container.removeEventListener('scroll', onScroll);
  }, [groupHasMore, groupLoading]);

  const handleEdit = (item) => {
    setNewGroup(item);
    setEditingId(item.id);
    setShowModal(true);
  };

  useEffect(() => {
    const div = document.getElementById('imageBlock1');
    if (!div) return;

    if (showModal && newGroup.groupImage) {
      div.style.backgroundImage = `url(${process.env.REACT_APP_SPACES_BASE}/${newGroup.groupImage})`;
      div.style.backgroundSize = 'cover';
      div.style.backgroundPosition = 'center';
    } else {
      div.style.backgroundImage = '';
    }
  }, [showModal, newGroup.groupImage]);

  const handleSave = async () => {
    if (!runValidation()) return;
    try {
      const { exists } = await checkGroupNameExists({
        groupName: newGroup.groupName,
        excludeId: editingId,
      });
      if (exists) {
        setErrors((prev) => ({ ...prev, groupName: 'Group name already exists' }));
        return;
      }

      const cleanPayload = {};
      for (const key in newGroup) {
        if (newGroup[key] !== null && typeof newGroup[key] !== 'object') {
          cleanPayload[key] = newGroup[key];
        }
      }

      if (editingId) {
        await updateGroup(editingId, cleanPayload);
      } else {
        await createGroup(cleanPayload);
      }

      setShowModal(false);
      setEditingId(null);

      setNewGroup({});
      setGroupList([]);
      setGroupPage(1);
      setGroupHasMore(true);
      await fetchGroups();
    } catch (error) {
      console.error('Error saving group:', error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this group?')) return;
    await deleteGroup(id);
    setGroupList([]);
    setGroupPage(1);
    setGroupHasMore(true);
    await fetchGroups();
  };

  const generateCsv = () => {
    const headers = ['Model or Group Name', 'Class', 'Manufacturer'];
    const rows = groupList.map((row) => [
      row.groupName || '',
      row.class?.className || '',
      row.manufacturer?.manufacturer || '',
    ]);
    const csvLines = [
      headers.join(','),
      ...rows.map((r) =>
        r.map((v) => `"${String(v ?? '').replace(/"/g, '""')}"`).join(',')
      ),
    ];
    return csvLines.join('\n');
  };

  const handleExportExcel = () => {
    const headers = ['Modlel or Group Name', 'Class', 'Manufacturer'];
    const rows = groupList.map((row) => [
      row.groupName || '',
      row.class?.className || '',
      row.manufacturer?.manufacturer || '',
    ]);
    const worksheetData = [headers, ...rows];
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Groups');
    const wbout = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([wbout], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'groups.xlsx';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleExportCSV = () => {
    const csv = generateCsv();
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'groups.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="card h-100 p-0 rounded-0">
      <div className="card-header d-flex justify-content-between align-items-center">
        <div className="d-flex flex-wrap align-items-center gap-2 ms-3">
          <div className="dropdown">
            <button
              className="w-32-px h-32-px bg-secondary-focus text-secondary-main rounded-circle d-inline-flex align-items-center justify-content-center me-6"
              type="button"
              onClick={() => setShowDropdown((prev) => !prev)}
            >
              <Icon icon="mage:filter-2" className="icon text-xl line-height-1" />
            </button>
            <button
              id="reset-filters-btn"
              className="w-32-px h-32-px bg-secondary-focus text-secondary-main rounded-circle d-inline-flex align-items-center justify-content-center me-6"
              onClick={resetFilters}
            >
              <Icon icon="mage:multiply" className="icon text-xl line-height-1" />
            </button>
            <button
              id="apply-filters-btn"
              className="d-none w-32-px h-32-px bg-secondary-focus text-secondary-main rounded-circle d-inline-flex align-items-center justify-content-center me-6"
              onClick={applyFilters}
            >
              <Icon icon="mage:check" className="icon text-xl line-height-1" />
            </button>
            {renderFilterBadges()}
            {showDropdown && renderFilterDropdown()}
          </div>
        </div>

        <div className="d-flex align-items-center gap-2 ms-auto me-8">
          <input
            type="text"
            className="form-control form-control-sm"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="dropdown me-2">
          <button
            className="btn btn-outline-secondary text-sm btn-sm px-16 py-12 radius-4 dropdown-toggle"
            type="button"
            data-bs-toggle="dropdown"
            aria-expanded="false"
          >
            Export Data
          </button>
          <ul className="dropdown-menu p-12 border bg-base shadow">
            <li>
              <button className="dropdown-item" onClick={handleExportExcel}>
                Excel
              </button>
            </li>
            <li>
              <button className="dropdown-item" onClick={handleExportCSV}>
                CSV
              </button>
            </li>
          </ul>
        </div>

        {userProfile?.privileges?.includes('groups:create') && (
          <button
            className="btn btn-primary text-sm btn-sm px-16 py-12 radius-4 d-flex align-items-center gap-2"
            onClick={() => {
              setNewGroup({});
              setEditingId(null);
              setShowModal(true);
            }}
          >
            <Icon icon="ic:baseline-plus" className="icon text-xl line-height-1" />
            Add Group
          </button>
        )}
      </div>

      <div className="card-body p-0">
        <div
          className="paginated-table-container scroll-sm"
          ref={listContainerRef}
          style={{ maxHeight: 'calc(100vh - 205px)' }}
        >
          <GroupDataTable
            groups={groupList}
            onEdit={handleEdit}
            onDelete={handleDelete}
            loading={groupLoading}
          />
        </div>
      </div>

      {showModal && (
        <div className="modal show d-block" tabIndex="-1">
          <div className="modal-dialog modal-lg"><div className="modal-content">
            <div className="modal-header"><h5 className="modal-title">{editingId ? 'Edit Model or Group' : 'New Model or Group'}</h5><button className="btn-close" onClick={() => setShowModal(false)} /></div>
            <div className="modal-body">
              <div className="row g-3">
                {inputFields.map(renderField)}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Close</button>
              <button className="btn btn-primary" onClick={handleSave}>Save</button>
            </div>
          </div></div>
        </div>
      )}
    </div>
  );
};

export default GroupListLayer;
